/**
 * Thin, self-rolled front-end error reporter (no third-party SDK). Captures
 * uncaught errors and unhandled rejections, scrubs PII, samples, and POSTs a
 * minimal payload to the marketing Worker's /client-errors route, which forwards
 * to the platform log pipeline (Logpush → R2 is Paid-only and DEFERRED on the
 * Free plan; today the Worker just logs to Workers Logs). Version-tagged so a
 * report maps back to a deploy.
 *
 * Shares the shape intended for the PWA's reporter; converge them when that lands.
 */
import { scrubPii } from '@shopassistant/shared/scrub';

const RELEASE_VERSION = import.meta.env.VITE_RELEASE_VERSION ?? 'dev';
const SAMPLE_RATE = 1; // 0..1 — send everything for now (low-traffic static site)
const ENDPOINT = '/client-errors';
const MAX_STACK = 2000;

interface ClientErrorReport {
  message: string;
  source: 'error' | 'unhandledrejection';
  path: string;
  stack?: string;
  release: string;
}

function send(report: ClientErrorReport): void {
  try {
    const body = JSON.stringify(report);
    // sendBeacon survives page unload; fall back to fetch with keepalive.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(ENDPOINT, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'content-type': 'application/json' },
      });
    }
  } catch {
    /* best-effort telemetry — never let reporting throw */
  }
}

function report(message: string, source: ClientErrorReport['source'], stack?: string): void {
  if (Math.random() > SAMPLE_RATE) return;
  send({
    message: scrubPii(message).slice(0, 500),
    source,
    path: location.pathname,
    ...(stack ? { stack: scrubPii(stack).slice(0, MAX_STACK) } : {}),
    release: RELEASE_VERSION,
  });
}

export function initErrorReporter(): void {
  window.addEventListener('error', (e) => {
    report(e.message || 'Uncaught error', 'error', e.error?.stack);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    report(
      message || 'Unhandled rejection',
      'unhandledrejection',
      reason instanceof Error ? reason.stack : undefined,
    );
  });
}
